import type { DiagramView, GraphIssue, InterviewDto, InterviewEventDto, ProcessGraph, TranscriptEntryDto } from '@process-hub/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Performer } from '../components/Badges';
import { describeError } from '../errors';
import { MermaidDiagram } from '../components/MermaidDiagram';
import { useDictation } from '../speech';

/**
 * The interview canvas (design/Process Platform.dc.html): conversation on the left, the record
 * building on the right after every answer. The right side is the draft rows as the API
 * persisted them — never a client-side copy — so what the owner sees is what will be saved.
 */

type Correction = Extract<InterviewEventDto, { type: 'graph' }>['corrections'][number];

export function Interview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { file?: File } | null };
  const pendingFile = useRef<File | null>(location.state?.file instanceof File ? location.state.file : null);
  const [data, setData] = useState<InterviewDto | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntryDto[]>([]);
  const [graph, setGraph] = useState<ProcessGraph | null>(null);
  const [issues, setIssues] = useState<GraphIssue[]>([]);
  const [diagrams, setDiagrams] = useState<Record<DiagramView, string> | null>(null);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<'steps' | 'diagram'>('steps');
  const log = useRef<HTMLDivElement>(null);
  // Voice capture (D-032): finished phrases append to the draft; the owner still presses Send.
  const spoken = useRef(false);
  const dictation = useDictation((text) => {
    if (!text) return;
    spoken.current = true;
    setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, '')} ${text}` : text));
  });

  useEffect(() => {
    api
      .getInterview(id)
      .then((d) => {
        setData(d);
        setTranscript(d.transcript);
        setGraph(d.graph);
        setIssues(d.issues);
        setDiagrams(d.diagrams);
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.status === 404 ? 'No such interview.' : describeError(e, 'The interview could not be loaded.')));
  }, [id]);

  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [transcript, streaming]);

  // A file chosen on the start screen arrives in history state; upload it once the canvas has loaded (D-030).
  useEffect(() => {
    if (data && !data.session.completedAt && pendingFile.current) {
      const f = pendingFile.current;
      pendingFile.current = null;
      void attach(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const message = draft.trim();
    if (!message || streaming !== null) return;
    setDraft('');
    if (dictation.listening) dictation.stop();
    const wasSpoken = spoken.current;
    spoken.current = false;
    await runStream(message, api.streamTurn(id, message, wasSpoken));
  }

  async function attach(file: File) {
    if (streaming !== null) return;
    await runStream(`Attached: ${file.name}`, api.streamDocument(id, file));
  }

  /** A screenshot pasted or dropped into the conversation (D-033). */
  async function pasteScreenshot(blob: Blob, name: string) {
    if (streaming !== null) return;
    const note = draft.trim() || null;
    setDraft('');
    await runStream(`Pasted a screenshot${note ? `: ${note}` : ''}`, api.streamScreenshot(id, blob, name, note));
  }

  function onPaste(e: React.ClipboardEvent) {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (file) {
      e.preventDefault();
      void pasteScreenshot(file, file.name || `pasted.${file.type.split('/')[1] ?? 'png'}`);
    }
  }

  function onDrop(e: React.DragEvent) {
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void pasteScreenshot(file, file.name);
    }
  }

  async function runStream(userLine: string, events: AsyncGenerator<InterviewEventDto>) {
    setNotice(null);
    setCorrections([]);
    setTranscript((t) => [...t, { role: 'user', text: userLine }]);
    setStreaming('');
    let assistant = '';
    try {
      for await (const ev of events) {
        switch (ev.type) {
          case 'document':
            setTranscript((t) => [
              ...t.slice(0, -1),
              {
                role: 'user',
                text: `Attached: ${ev.fileName} — ${ev.pages ? `${ev.pages} page${ev.pages === 1 ? '' : 's'}` : `${ev.chars.toLocaleString()} characters`} read${ev.truncated ? ' (long document, truncated)' : ''}${ev.images ? `, ${ev.images} screenshot${ev.images === 1 ? '' : 's'}` : ''}${ev.skippedImages ? ` (${ev.skippedImages} image${ev.skippedImages === 1 ? '' : 's'} could not be imported)` : ''}. Drafting the process from it.`,
              },
            ]);
            break;
          case 'screenshot':
            setTranscript((t) => [...t.slice(0, -1), { role: 'user', text: `Pasted Screenshot ${ev.seq} (${ev.fileName}).` }]);
            break;
          case 'tool':
            if (ev.name === 'assign_screenshots') setNotice(ev.summary);
            break;
          case 'text':
            assistant += ev.delta;
            setStreaming(assistant);
            break;
          case 'graph':
            setGraph(ev.graph);
            setIssues(ev.issues);
            setDiagrams(ev.diagrams);
            setCorrections(ev.corrections);
            break;
          case 'refusal':
            setNotice('The interviewer declined to continue with that message. Rephrase and try again.');
            break;
          case 'error':
            setNotice(ev.message);
            break;
          case 'done':
            break;
        }
      }
    } catch (err) {
      setNotice(describeError(err, 'The connection to the interviewer was lost. Your last message was not recorded; send it again.'));
    } finally {
      if (assistant) setTranscript((t) => [...t, { role: 'assistant', text: assistant }]);
      setStreaming(null);
    }
  }

  async function finish() {
    try {
      const r = await api.finishInterview(id);
      navigate(`/processes/${r.slug}`);
    } catch (err) {
      setNotice(describeError(err, 'Could not finish the interview.'));
    }
  }

  if (error)
    return (
      <div className="notice notice--error">
        {error} <Link to="/">Back to the library</Link>
      </div>
    );
  if (!data) return <div className="notice">Loading the interview.</div>;

  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const finished = Boolean(data.session.completedAt);
  const realSteps = graph?.steps.filter((s) => s.key !== 'process_starts' && s.key !== 'process_ends') ?? [];

  return (
    <div className="interview">
      <section className="card interview__chat">
        <div className="card__header">
          <div>
            <div className="card__title">{graph?.name ?? data.process.name}</div>
            <div className="grid__secondary">
              {data.process.departmentName} · maintained by {data.process.maintainerRole} · prompt {data.session.promptVersion ?? '—'}
            </div>
          </div>
          <div className="card__meta">
            {finished ? (
              <Link to={`/processes/${data.process.slug}`}>Open the process</Link>
            ) : (
              <button type="button" className="button button--secondary" onClick={finish} disabled={streaming !== null || realSteps.length === 0} title={realSteps.length === 0 ? 'Nothing recorded yet' : undefined}>
                Finish and review
              </button>
            )}
          </div>
        </div>
        <div className="chat" ref={log} aria-live="polite" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
          {transcript.length === 0 && streaming === null && (
            <div className="chat__hint">Say hello, or start with what sets this process in motion. The interviewer asks the rest.</div>
          )}
          {transcript.map((m, i) => (
            <div key={i} className={`bubble bubble--${m.role}`}>
              {m.text}
            </div>
          ))}
          {streaming !== null && <div className="bubble bubble--assistant bubble--streaming">{streaming || '…'}</div>}
        </div>
        {notice && <div className="notice notice--error" style={{ margin: '0 18px 12px' }}>{notice}</div>}
        {dictation.error && <div className="notice notice--error" style={{ margin: '0 18px 12px' }}>{dictation.error}</div>}
        {dictation.listening && dictation.interim && (
          <div className="muted" style={{ margin: '0 18px 6px', fontStyle: 'italic' }} aria-live="polite">
            {dictation.interim}…
          </div>
        )}
        {!finished && (
          <form className="composer" onSubmit={send}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
                }
              }}
              placeholder={dictation.listening ? (dictation.interim ? `${dictation.interim}…` : 'Listening. Speak; finished phrases appear here. Press Send when done.') : 'Describe what happens. Enter to send, Shift+Enter for a new line. Paste a screenshot to attach it to the step you are discussing.'}
              rows={2}
              disabled={streaming !== null}
            />
            {dictation.supported && (
              <button
                type="button"
                className={`button ${dictation.listening ? 'button--danger' : 'button--secondary'}`}
                aria-pressed={dictation.listening}
                title={dictation.listening ? 'Stop listening' : 'Dictate instead of typing (Edge or Chrome)'}
                disabled={streaming !== null}
                onClick={() => (dictation.listening ? dictation.stop() : dictation.start())}
              >
                {dictation.listening ? 'Stop' : 'Speak'}
              </button>
            )}
            <button type="submit" className="button" disabled={streaming !== null || !draft.trim()}>
              Send
            </button>
            <label className="button button--secondary" title="Attach an existing SOP, checklist, work instruction or Scribe export (PDF, Word, HTML, text, Markdown) — screenshots come along" style={{ cursor: streaming !== null ? 'default' : 'pointer' }}>
              Attach SOP
              <input
                type="file"
                accept=".pdf,.docx,.html,.htm,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/html,text/plain,text/markdown"
                hidden
                disabled={streaming !== null}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void attach(f);
                }}
              />
            </label>
          </form>
        )}
      </section>

      <aside className="card interview__record">
        <div className="tabs">
          <button type="button" role="tab" aria-selected={panel === 'steps'} className={`tab${panel === 'steps' ? ' tab--active' : ''}`} onClick={() => setPanel('steps')}>
            Record so far {realSteps.length ? `(${realSteps.length})` : ''}
          </button>
          <button type="button" role="tab" aria-selected={panel === 'diagram'} className={`tab${panel === 'diagram' ? ' tab--active' : ''}`} onClick={() => setPanel('diagram')} disabled={!diagrams || realSteps.length === 0}>
            Diagram
          </button>
        </div>
        {corrections.length > 0 && (
          <div className="issue">
            Set to human by the human-default rule: {corrections.map((c) => `${c.stepName} (${c.reasons.join(', ')})`).join('; ')}. The owner can override this in review, with a reason.
          </div>
        )}
        {errors.map((i, n) => (
          <div key={`e${n}`} className="issue issue--error">
            {i.message}
          </div>
        ))}
        {warnings.map((i, n) => (
          <div key={`w${n}`} className="issue">
            {i.message}
          </div>
        ))}
        {panel === 'steps' &&
          (realSteps.length === 0 ? (
            <div className="notice" style={{ margin: 18 }}>
              Steps appear here as you describe them.
            </div>
          ) : (
            <ol className="record">
              {graph!.steps.map((s) => (
                <li key={s.key} className={`record__step record__step--${s.stepType}`}>
                  <div className="record__name">
                    {s.name}
                    {s.stepType === 'decision' && <span className="tag tag--decision">DECISION</span>}
                    {s.stepType === 'wait' && <span className="tag">WAIT</span>}
                  </div>
                  <div className="record__meta">
                    <span>{s.role}</span>
                    <Performer value={s.performedBy} />
                    {s.systems.length > 0 && <span className="muted">{s.systems.map((x) => x.name).join(', ')}</span>}
                  </div>
                </li>
              ))}
            </ol>
          ))}
        {panel === 'diagram' && diagrams && <MermaidDiagram source={diagrams.swimlane} />}
        {graph && graph.openQuestions.length > 0 && (
          <div className="record__questions">
            <div className="record__questions-title">Open questions</div>
            <ul>
              {graph.openQuestions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}
